/**
 * Format Manager - Enhanced Admin interface for output formatting configurations
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import EnhancedFormatForm from './FormatManagerEnhanced';
import TutorialOverlay from '../components/TutorialOverlay';
import './FormatManager.css';

// Enhanced Icons
const FormatIcon = ({ type, size = 20 }) => {
    const icons = {
        date: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
        ),
        number: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="9" x2="20" y2="9" />
                <line x1="4" y1="15" x2="20" y2="15" />
                <line x1="10" y1="3" x2="8" y2="21" />
                <line x1="16" y1="3" x2="14" y2="21" />
            </svg>
        ),
        currency: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
        ),
        text: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="4,7 4,4 20,4 20,7" />
                <line x1="9" y1="20" x2="15" y2="20" />
                <line x1="12" y1="4" x2="12" y2="20" />
            </svg>
        ),
        conditional: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
            </svg>
        ),
        cleanup: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
        ),
        duplicate: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
        ),
        preview: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        ),
        magic: (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 4V2a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v2" />
                <path d="M15 4h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" />
                <circle cx="12" cy="11" r="2" />
                <path d="M12 7v8" />
                <path d="M8 11h8" />
            </svg>
        )
    };
    return icons[type] || icons.magic;
};

const FormatManager = () => {
    const { user } = useAuth();
    const [configurations, setConfigurations] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [showTutorial, setShowTutorial] = useState(false);
    const [activeSection, setActiveSection] = useState('basic');

    const tutorialSteps = [
        {
            targetId: 'tutorial-template-select',
            title: 'Select a Template',
            content: 'Start by choosing the spreadsheet template you want to format. We will automatically load its columns for you.',
            position: 'bottom',
            onEnter: () => setActiveSection('basic')
        },
        {
            targetId: 'tutorial-config-name',
            title: 'Name Your Configuration',
            content: 'Give your formatting set a name so you can reuse it later or apply it to future uploads.',
            position: 'bottom',
            onEnter: () => setActiveSection('basic')
        },
        {
            targetId: 'tutorial-presets-select',
            title: 'Magic Presets',
            content: 'Not sure where to start? Apply a preset like "Financial Report" to instantly configure professional rules.',
            position: 'bottom',
            onEnter: () => setActiveSection('basic')
        },
        {
            targetId: 'tutorial-preview-btn',
            title: 'Live Preview',
            content: 'Once configured, click here to see your changes applied to real data side-by-side. No more guesswork!',
            position: 'top'
        },
        {
            targetId: 'tutorial-save-btn',
            title: 'Save Your Work',
            content: 'Happy with your settings? Click here to save the configuration and start using it for your uploads.',
            position: 'top'
        }
    ];

    useEffect(() => {
        const hasSeen = localStorage.getItem('hasSeenFormattingTutorial');
        if (!hasSeen && showCreateForm) {
            // Give the UI a moment to load before starting
            const timer = setTimeout(() => setShowTutorial(true), 1500);
            return () => clearTimeout(timer);
        }
    }, [showCreateForm]);

    const handleTutorialComplete = () => {
        setShowTutorial(false);
        localStorage.setItem('hasSeenFormattingTutorial', 'true');
    };

    // Enhanced form state with better organization
    const [formData, setFormData] = useState({
        template_id: '',
        name: '',
        description: '',
        config_data: {
            standardization: {
                dates: {
                    enabled: true,
                    output_format: 'YYYY-MM-DD',
                    timezone: 'UTC',
                    input_formats: ['MM/DD/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']
                },
                numbers: {
                    enabled: true,
                    decimal_places: 2,
                    thousand_separator: ',',
                    decimal_separator: '.',
                    scientific_threshold: 1000000
                },
                currency: {
                    enabled: true,
                    symbol: '$',
                    position: 'before',
                    decimal_places: 2,
                    code: 'USD'
                },
                text: {
                    enabled: true,
                    capitalization: 'none',
                    trim_whitespace: true,
                    encoding: 'utf-8'
                }
            },
            cleanup: {
                enabled: true,
                remove_empty_rows: true,
                remove_empty_columns: true,
                merge_duplicate_headers: true,
                group_similar_data: false
            },
            highlighting: {
                duplicates: {
                    enabled: true,
                    color: '#ffeb3b',
                    action: 'highlight'
                },
                inconsistencies: {
                    enabled: true,
                    color: '#f44336',
                    action: 'flag'
                }
            },
            conditional_formatting: [],
            reports: {
                summary_enabled: true,
                executive_summary: false,
                audit_report: true,
                custom_reports: []
            },
            sorting: {
                sortBy: [],
                groupBy: '',
                outputType: 'single_sheet_grouped',
                showSectionHeaders: true,
                showSubtotals: true,
                showGrandTotal: true,
                colorScheme: 'default'
            },
            export: {
                format: 'xlsx',
                include_headers: true,
                freeze_header_row: false,
                auto_column_width: false,
                add_filters: false
            }
        }
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([loadConfigurations(), loadTemplates()]);
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    };

    const loadConfigurations = async () => {
        const response = await fetch('/api/formatting/configs', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load configurations');
        }

        const data = await response.json();
        setConfigurations(data.configs);
    };

    const loadTemplates = async () => {
        const response = await fetch('/api/templates', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load templates');
        }

        const data = await response.json();
        setTemplates(data.templates || []);
    };

    const handleCreateConfig = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('/api/formatting/configs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                // The error response has { error, message, details }
                const errorMessage = errorData.message || errorData.error || 'Failed to create configuration';
                throw new Error(errorMessage);
            }

            setMessage({ type: 'success', text: 'Configuration created successfully' });
            setShowCreateForm(false);
            resetForm();
            loadConfigurations();

        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleActivateConfig = async (configId) => {
        try {
            const response = await fetch(`/api/formatting/configs/${configId}/activate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to activate configuration');
            }

            setMessage({ type: 'success', text: 'Configuration activated' });
            loadConfigurations();

        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleDeactivateConfig = async (configId) => {
        try {
            const response = await fetch(`/api/formatting/configs/${configId}/deactivate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to deactivate configuration');
            }

            setMessage({ type: 'success', text: 'Configuration deactivated' });
            loadConfigurations();

        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handlePreview = async (customData) => {
        const defaultSampleData = [
            {
                date: '2024-01-15',
                amount: 1234.567,
                status: 'active',
                description: '  sample text  '
            },
            {
                date: '15/01/2024',
                amount: 9876.54,
                status: 'inactive',
                description: 'another sample'
            }
        ];

        const sampleData = customData || defaultSampleData;

        try {
            const response = await fetch('/api/formatting/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    config_data: formData.config_data,
                    sample_data: sampleData
                })
            });

            if (!response.ok) {
                throw new Error('Preview failed');
            }

            const data = await response.json();
            setPreviewData(data);

        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const resetForm = () => {
        setFormData({
            template_id: '',
            name: '',
            description: '',
            config_data: {
                standardization: {
                    dates: {
                        enabled: true,
                        output_format: 'YYYY-MM-DD',
                        timezone: 'UTC',
                        input_formats: ['MM/DD/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']
                    },
                    numbers: {
                        enabled: true,
                        decimal_places: 2,
                        thousand_separator: ',',
                        decimal_separator: '.',
                        scientific_threshold: 1000000
                    },
                    currency: {
                        enabled: true,
                        symbol: '$',
                        position: 'before',
                        decimal_places: 2,
                        code: 'USD'
                    },
                    text: {
                        enabled: true,
                        capitalization: 'none',
                        trim_whitespace: true,
                        encoding: 'utf-8'
                    }
                },
                cleanup: {
                    enabled: true,
                    remove_empty_rows: true,
                    remove_empty_columns: true,
                    merge_duplicate_headers: true,
                    group_similar_data: false
                },
                highlighting: {
                    duplicates: { enabled: true, color: '#ffeb3b', action: 'highlight' },
                    inconsistencies: { enabled: true, color: '#f44336', action: 'flag' }
                },
                conditional_formatting: [],
                reports: {
                    summary_enabled: true,
                    executive_summary: false,
                    audit_report: true,
                    custom_reports: []
                }
            }
        });
        setPreviewData(null);
    };

    if (user?.role !== 'admin') {
        return (
            <div className="format-manager">
                <div className="access-denied">
                    <h2>Access Denied</h2>
                    <p>Only administrators can manage formatting configurations.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="format-manager">
            <header className="format-manager-header">
                <div className="header-content">
                    <div className="header-title">
                        <FormatIcon type="magic" size={32} />
                        <div>
                            <h1>Output Formatting Studio</h1>
                            <p>Transform raw data into professional, readable outputs</p>
                        </div>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={() => loadData()}
                        disabled={loading}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M23 4v6h-6" />
                            <path d="M1 20v-6h6" />
                            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                        </svg>
                        Refresh
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => {
                            setShowCreateForm(true);
                            setActiveSection('basic');
                            // Brief delay to allow form to mount
                            setTimeout(() => setShowTutorial(true), 1500);
                        }}
                        title="Show Tutorial"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        Guide
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateForm(true)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Create Configuration
                    </button>
                </div>
            </header>

            {message && (
                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                    <div className="alert-content">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {message.type === 'success' ? (
                                <>
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="9,11 12,14 22,4" />
                                </>
                            ) : (
                                <>
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="15" y1="9" x2="9" y2="15" />
                                    <line x1="9" y1="9" x2="15" y2="15" />
                                </>
                            )}
                        </svg>
                        <span>{message.text}</span>
                    </div>
                    <button
                        className="alert-close"
                        onClick={() => setMessage(null)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Configuration List */}
            {!showCreateForm && (
                <div className="configurations-list">
                    <div className="list-header">
                        <h2>Formatting Configurations</h2>
                        <p>Manage output formatting rules for your templates</p>
                    </div>

                    {loading ? (
                        <div className="loading">Loading configurations...</div>
                    ) : (
                        <div className="config-grid">
                            {configurations.map(config => (
                                <div key={config.id} className="config-card">
                                    <div className="config-header">
                                        <h3>{config.name}</h3>
                                        <div className="config-status">
                                            {config.is_active ? (
                                                <span className="status-badge active">Active</span>
                                            ) : (
                                                <span className="status-badge inactive">Inactive</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="config-details">
                                        <p><strong>Template:</strong> {config.template_name}</p>
                                        <p><strong>Version:</strong> {config.version}</p>
                                        <p><strong>Created by:</strong> {config.created_by}</p>
                                        {config.description && (
                                            <p><strong>Description:</strong> {config.description}</p>
                                        )}
                                    </div>

                                    <div className="config-actions">
                                        {config.is_active ? (
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => handleDeactivateConfig(config.id)}
                                            >
                                                Deactivate
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleActivateConfig(config.id)}
                                            >
                                                Activate
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-outline btn-sm"
                                            onClick={() => setSelectedConfig(config)}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Enhanced Create/Edit Form */}
            {showCreateForm && (
                <EnhancedFormatForm
                    formData={formData}
                    setFormData={setFormData}
                    templates={templates}
                    onSubmit={handleCreateConfig}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    onCancel={() => {
                        setShowCreateForm(false);
                        resetForm();
                        setActiveSection('basic');
                    }}
                    onPreview={handlePreview}
                    previewData={previewData}
                    loading={loading}
                />
            )}

            {/* Configuration Details Modal */}
            {selectedConfig && (
                <div className="modal-overlay" onClick={() => setSelectedConfig(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{selectedConfig.name}</h3>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setSelectedConfig(null)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                                Close
                            </button>
                        </div>
                        <div className="modal-body">
                            <pre>{JSON.stringify(selectedConfig.config_data, null, 2)}</pre>
                        </div>
                    </div>
                </div>
            )}
            {/* Tutorial Overlay */}
            {showTutorial && showCreateForm && (
                <TutorialOverlay
                    steps={tutorialSteps}
                    onComplete={handleTutorialComplete}
                    onClose={() => setShowTutorial(false)}
                />
            )}
        </div>
    );
};

export default FormatManager;